import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { useSystemConfig, useDataLoadingStatus, useSystemId, useSelectedDateTimeFilter } from "../../../hooks/useReduxSelectors";
import { useMsal } from "Auth/msal-context";
import {
  loadInfluxData,
  checkInfluxLoadStatus,
  triggerHotpathBackfill,
  setDateTimeFilter,
  setAllLoadingTrue,
} from "../../../ducks/trendsDuck";

import { Box, Stack, Typography, TextField } from "@mui/material";
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { devPanel } from "../../../../theme/muiTheme";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";
import { Button as MuiButton } from "@mui/material";

// Constants
const EDGE = "Edge";
const CLOUD = "Cloud";
const FILTERS = ["Live", "Hour", "Day", "Week", "Month"];

const DateTimeFilter = ({ appEnvironment, getTrendsData }) => {
  const systemId = useSystemId();
  const dispatch = useDispatch();
  const { user } = useMsal();
  const selectedDateTimeFilter = useSelectedDateTimeFilter();
  const config = useSystemConfig();

  // Check if user has Admin role (Role.OverSight.Admin)
  const isAdmin = user?.idTokenClaims?.roles?.some(role => role === "Role.OverSight.Admin") || false;
  const dataLoadingStatus = useDataLoadingStatus() || {
    isLoading: false,
    loadProgress: 0,
    writeProgress: 0,
    status: null,
    error: null,
    elapsedSeconds: 0,
    message: null,
    loading_type: null,
    gaps_total: 0,
    gaps_processed: 0,
    availableDataRange: {
      from: null,
      to: null,
      sources: []
    }
  };


  const [monthsToLoad, setMonthsToLoad] = useState(1);
  const [pollTimeout, setPollTimeout] = useState(null);
  const [isStatusChecking, setIsStatusChecking] = useState(false);
  const [useTimeRange, setUseTimeRange] = useState(false); // Toggle between months and specific time range

  // DateTime picker values (using dayjs objects for MUI DateTimePicker)
  const [startDateTime, setStartDateTime] = useState(null);
  const [endDateTime, setEndDateTime] = useState(null);

  // Ref to prevent useEffect chain from re-dispatching when a filter button click
  // already dispatched the correct values to Redux.
  const filterClickRef = useRef(false);

  // Sync datetime picker display when selectedDateTimeFilter changes
  // (e.g. on initial mount). Skipped when the click handler already set the values.
  useEffect(() => {
    if (filterClickRef.current) return;
    if (selectedDateTimeFilter === "Custom") return;

    const now = dayjs();
    let start, end;

    switch (selectedDateTimeFilter) {
      case "Live":
        start = now.subtract(30, 'minutes');
        end = now;
        break;
      case "Hour":
        start = now.subtract(1, 'hour');
        end = now;
        break;
      case "Day":
        start = now.subtract(1, 'day');
        end = now;
        break;
      case "Week":
        start = now.subtract(1, 'week');
        end = now;
        break;
      case "Month":
        start = now.subtract(1, 'month');
        end = now;
        break;
      default:
        if (selectedDateTimeFilter && selectedDateTimeFilter !== "Custom") {
          start = now.subtract(1, 'hour');
          end = now;
        } else {
          return;
        }
    }

    setStartDateTime(start);
    setEndDateTime(end);
  }, [selectedDateTimeFilter]);

  // Sync manual datetime picker changes to Redux.
  // Skipped when the click handler already dispatched to Redux.
  useEffect(() => {
    if (filterClickRef.current) {
      filterClickRef.current = false;
      return;
    }
    if (!startDateTime || !endDateTime) return;

    if (!dayjs.isDayjs(startDateTime) || !startDateTime.isValid() ||
      !dayjs.isDayjs(endDateTime) || !endDateTime.isValid()) {
      console.warn('Invalid datetime values:', startDateTime, endDateTime);
      return;
    }

    const startTimestamp = Math.floor(startDateTime.valueOf() / 1000);
    const endTimestamp = Math.floor(endDateTime.valueOf() / 1000);

    dispatch(setDateTimeFilter({
      systemId,
      data: selectedDateTimeFilter,
      directTimestampFrom: startTimestamp,
      directTimestampTo: endTimestamp
    }));
    dispatch(setAllLoadingTrue({ systemId }));
  }, [startDateTime, endDateTime, dispatch, systemId]); // eslint-disable-line react-hooks/exhaustive-deps


  const onClickHandler = useCallback(
    (filterVal, oldVal) => {
      if (!filterVal || filterVal === oldVal) return;

      const now = dayjs();
      let start, end;
      switch (filterVal) {
        case "Live": start = now.subtract(30, 'minutes'); end = now; break;
        case "Hour": start = now.subtract(1, 'hour'); end = now; break;
        case "Day": start = now.subtract(1, 'day'); end = now; break;
        case "Week": start = now.subtract(1, 'week'); end = now; break;
        case "Month": start = now.subtract(1, 'month'); end = now; break;
        default: start = now.subtract(1, 'hour'); end = now;
      }

      const fromTs = Math.floor(start.valueOf() / 1000);
      const toTs = Math.floor(end.valueOf() / 1000);

      // Mark ref so the useEffect chain doesn't re-dispatch
      filterClickRef.current = true;
      setStartDateTime(start);
      setEndDateTime(end);

      dispatch(setAllLoadingTrue({ systemId }));
      dispatch(setDateTimeFilter({
        systemId,
        data: filterVal,
        directTimestampFrom: fromTs,
        directTimestampTo: toTs,
        minVal: -1,
        maxVal: 0,
      }));
    },
    [dispatch, systemId]
  );

  const reFetch = () => {
    dispatch(setAllLoadingTrue({ systemId }));
    getTrendsData(true);
  };

  // Check if hotpath has less than 90 days of data
  const isHotpathIncomplete = () => {
    if (!dataLoadingStatus.availableDataRange?.from || !dataLoadingStatus.availableDataRange?.to) {
      return false;
    }

    const fromMoment = dayjs(dataLoadingStatus.availableDataRange.from);
    const toMoment = dayjs(dataLoadingStatus.availableDataRange.to);
    const daysDiff = toMoment.diff(fromMoment, 'days');

    return daysDiff < 90;
  };

  // Handle hotpath backfill
  const handleHotpathBackfill = async () => {
    if (!config) return;

    const body = {
      SiteId: config.CUSTOMER_ID,
      EdgeUnitId: config.DEVICE_ID
    };

    try {
      dispatch(triggerHotpathBackfill({ systemId, body }));
    } catch (error) {
      console.error('Failed to trigger hotpath backfill:', error);
    }
  };



  const isStartDateOutsideAvailability = () => {
    if (!startDateTime || !dataLoadingStatus.availableDataRange?.from) {
      return false; // If no data available or no start date, don't show load box
    }

    const availabilityStartMoment = dayjs(dataLoadingStatus.availableDataRange.from);

    // Check if user's start date is older than available data start
    return startDateTime.isBefore(availabilityStartMoment);
  };

  // Handle data loading
  const handleLoadData = async () => {
    if (!config) return;

    let body;

    if (useTimeRange) {
      // Use specific time range from datetime pickers
      if (!startDateTime || !endDateTime) {
        alert('Please select both start and end dates for time range loading.');
        return;
      }

      const fromTimestamp = Math.floor(startDateTime.valueOf() / 1000);
      const toTimestamp = Math.floor(endDateTime.valueOf() / 1000);

      body = {
        SiteId: config.CUSTOMER_ID || "site123",
        EdgeUnitId: config.DEVICE_ID || "edgeunit456",
        From: fromTimestamp,
        To: toTimestamp
      };
    } else {
      // Use months to load (existing behavior)
      if (monthsToLoad > 12) {
        alert('Maximum 12 months of data can be loaded. Please select a value between 1-12 months.');
        return;
      }

      body = {
        SiteId: config.CUSTOMER_ID || "site123",
        EdgeUnitId: config.DEVICE_ID || "edgeunit456",
        MonthsToLoad: monthsToLoad
      };
    }

    try {
      // Start loading (this will be pending until complete)
      dispatch(loadInfluxData({ systemId, body }));

      // Remove highlight after starting data load
      setUseTimeRange(false);

      // Start polling immediately with the same body
      if (!pollTimeout) {
        startPolling(body);
      }
    } catch (error) {
      console.error('Failed to start data loading:', error);
    }
  };

  // Format elapsed time from server seconds
  const formatElapsedTime = (seconds) => {
    if (!seconds || seconds < 1) return "0s";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${remainingSeconds}s`;
  };

  // Format status text with comprehensive information
  const getStatusText = () => {
    if (!dataLoadingStatus.isLoading) {
      return '';
    }

    const elapsedTime = formatElapsedTime(dataLoadingStatus.elapsedSeconds);
    const totalPoints = dataLoadingStatus.totalPoints?.toLocaleString() || '0';
    const loadPercent = (dataLoadingStatus.loadProgress || 0).toFixed(1);
    const writePercent = (dataLoadingStatus.writeProgress || 0).toFixed(1);
    const loadedBlobs = dataLoadingStatus.loadedBlobs || 0;
    const totalBlobs = dataLoadingStatus.totalBlobs || 0;
    const writtenPoints = dataLoadingStatus.writtenPoints?.toLocaleString() || '0';

    // Use message from server if available, otherwise format based on status
    if (dataLoadingStatus.message) {
      let statusDisplay = dataLoadingStatus.message;

      // Add loading type if available
      if (dataLoadingStatus.loading_type) {
        statusDisplay = `${dataLoadingStatus.loading_type.toUpperCase()}: ${statusDisplay}`;
      }

      // Add progress information
      statusDisplay += ` | Load: ${loadPercent}%`;

      if (dataLoadingStatus.writeProgress > 0) {
        statusDisplay += `, Write: ${writePercent}%`;
      }

      // Add gaps information if available
      if (dataLoadingStatus.gaps_total > 0) {
        const gapsProcessed = dataLoadingStatus.gaps_processed || 0;
        statusDisplay += ` | Gaps: ${gapsProcessed}/${dataLoadingStatus.gaps_total}`;
      }

      statusDisplay += ` | ${elapsedTime}`;

      return statusDisplay;
    }

    // Fallback to original formatting if no message
    if (dataLoadingStatus.status === 'writing') {
      return `Writing: ${writePercent}% | ${writtenPoints} points written | ${totalPoints} points total | Elapsed: ${elapsedTime}`;
    } else if (dataLoadingStatus.status) {
      return `Loading: ${loadPercent}% | ${loadedBlobs}/${totalBlobs} blobs | Elapsed: ${elapsedTime}`;
    } else {
      return `Starting... | ${writtenPoints} written points | Elapsed: ${elapsedTime}`;
    }
  };

  const stopPolling = useCallback(() => {
    if (pollTimeout) {
      clearInterval(pollTimeout);
      setPollTimeout(null);
    }
    setIsStatusChecking(false);
  }, [pollTimeout]);

  // Auto-start polling if loading is detected (on mount or status change)
  useEffect(() => {
    if (dataLoadingStatus?.isLoading && !pollTimeout && config?.APP_ENV === 'Cloud') {
      const body = {
        SiteId: config.CUSTOMER_ID || "site123",
        EdgeUnitId: config.DEVICE_ID || "edgeunit456",
        MonthsToLoad: monthsToLoad
      };

      const interval = setInterval(async () => {
        if (!isStatusChecking) {
          setIsStatusChecking(true);
          try {
            await dispatch(checkInfluxLoadStatus({ systemId, body }));
          } finally {
            setIsStatusChecking(false);
          }
        }
      }, 2000);
      setPollTimeout(interval);
    }
  }, [dataLoadingStatus?.isLoading, pollTimeout, monthsToLoad, dispatch, config]);

  // Format available data range display
  const getDataAvailabilityText = () => {
    if (!dataLoadingStatus.availableDataRange?.from || !dataLoadingStatus.availableDataRange?.to) {
      return 'Data availability: Unknown';
    }

    const fromDate = dayjs(dataLoadingStatus.availableDataRange.from).format('DD-MM-YYYY HH:mm');
    const toDate = dayjs(dataLoadingStatus.availableDataRange.to).format('DD-MM-YYYY HH:mm');
    const sources = dataLoadingStatus.availableDataRange.sources;

    let baseText = `Available data: ${fromDate} to ${toDate}`;

    if (sources && sources.length > 0) {
      const sourcesText = sources.join(', ');
      baseText += ` | Sources: ${sourcesText}`;
    }

    return baseText;
  };

  // Handle datetime input changes
  const handleStartDateTimeChange = (newValue) => {
    setStartDateTime(newValue);
    // Clear button selection when user manually changes datetime
    if (selectedDateTimeFilter !== "Custom") {
      dispatch(setDateTimeFilter({
        systemId,
        data: "Custom"
      }));
    }
  };

  const handleEndDateTimeChange = (newValue) => {
    setEndDateTime(newValue);
    // Clear button selection when user manually changes datetime  
    if (selectedDateTimeFilter !== "Custom") {
      dispatch(setDateTimeFilter({
        systemId,
        data: "Custom"
      }));
    }
  };

  const renderButton = (filter) => (
    <MuiButton
      onClick={() => onClickHandler(filter, selectedDateTimeFilter)}
      disabled={appEnvironment === EDGE && filter === "Year"}
      key={filter}
      size="small"
      sx={{ width: 40 }}
      variant={selectedDateTimeFilter === filter ? "contained" : "outlined"}
    >
      {filter}
    </MuiButton>
  );

  // Check for existing loading operation on component mount
  useEffect(() => {
    if (config && config.APP_ENV === 'Cloud') {
      console.log(config)
      const body = {
        SiteId: config.CUSTOMER_ID || "site123",
        EdgeUnitId: config.DEVICE_ID || "edgeunit456",
        MonthsToLoad: monthsToLoad
      };

      // Check status on initial load
      const checkStatus = async () => {
        if (!isStatusChecking) {
          setIsStatusChecking(true);
          try {
            await dispatch(checkInfluxLoadStatus({ systemId, body }));
          } finally {
            setIsStatusChecking(false);
          }
        }
      };
      checkStatus();
    }
  }, [config, dispatch, systemId, monthsToLoad]);

  // Handle loading completion
  useEffect(() => {
    if ((dataLoadingStatus?.loadProgress === 100 && dataLoadingStatus?.writeProgress === 100) && !dataLoadingStatus?.isLoading) {
      stopPolling();
      dispatch(setAllLoadingTrue({ systemId }));
    }
  }, [dataLoadingStatus?.loadProgress, dataLoadingStatus?.writeProgress, dataLoadingStatus?.isLoading, stopPolling, dispatch, systemId]);



  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);



  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        {/* Data Availability Info */}
        {config && config.APP_ENV === 'Cloud' &&
          <Typography variant="body2" sx={{
            mb: 1,
            fontSize: '12px',
            color: devPanel.success,
            fontStyle: 'italic'
          }}>
            {getDataAvailabilityText()}
          </Typography>}
        {dataLoadingStatus.isLoading && (<Typography variant="caption" sx={{ mb: 1, color: dataLoadingStatus.isLoading ? devPanel.infoBlue : devPanel.success }}>
          {getStatusText()}
        </Typography>)}
        <Box sx={{ display: "flex", flexDirection: "row", gap: 1, mb: 1, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
          {FILTERS.map(filter => renderButton(filter))}
          <MuiButton
            onClick={reFetch}
            size="small"
            sx={{ width: 80 }}
          >
            ReLoad
          </MuiButton>
          {/* Hotpath Backfill Button - Only visible for Admin users when hotpath has < 90 days */}
          {isAdmin && config?.APP_ENV === 'Cloud' && isHotpathIncomplete() && (
            <MuiButton
              onClick={handleHotpathBackfill}
              size="small"
              variant="outlined"
              color="warning"
              disabled={dataLoadingStatus.isLoading}
              sx={{ minWidth: 120, fontSize: '11px' }}
            >
              Backfill Hotpath
            </MuiButton>
          )}
          {/* Data Loading Controls - Only show when start date is outside available data range */}
          {isStartDateOutsideAvailability() && (
            <>
              <Typography variant="caption" sx={{ color: devPanel.warning, mb: 1, textAlign: 'center' }}>
                Selected start date is before available data range. Data loading required. Max last 12 months.
              </Typography>
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: 1,
                marginBottom: 1,
                gap: 1,
                padding: 1,
                background: devPanel.background,
                borderRadius: 1,
                fontSize: '12px'
              }}>
                {/* Toggle between months and time range */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
                  <Typography variant="caption" sx={{ color: 'white', fontSize: '11px' }}>Last months</Typography>
                  <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px' }}>
                    <input
                      type="checkbox"
                      checked={useTimeRange}
                      onChange={(e) => setUseTimeRange(e.target.checked)}
                      style={{ opacity: 0, width: 0, height: 0 }}
                      disabled={dataLoadingStatus.isLoading}
                    />
                    <span style={{
                      position: 'absolute',
                      cursor: 'pointer',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: useTimeRange ? devPanel.infoBlue : devPanel.border,
                      transition: '0.3s',
                      borderRadius: '24px'
                    }}>
                      <span style={{
                        position: 'absolute',
                        content: '',
                        height: '18px',
                        width: '18px',
                        left: useTimeRange ? '23px' : '3px',
                        bottom: '3px',
                        backgroundColor: 'white',
                        transition: '0.3s',
                        borderRadius: '50%'
                      }}></span>
                    </span>
                  </label>
                  <Typography variant="caption" sx={{ color: 'white', fontSize: '11px' }}>Time range</Typography>
                </Box>

                {/* Conditional inputs based on toggle */}
                {!useTimeRange ? (
                  // Last months mode
                  <>
                    <Typography variant="caption" sx={{ color: 'white' }}>Load months (max 12):</Typography>
                    <input
                      type="number"
                      min="1"
                      max="12"
                      value={monthsToLoad}
                      onChange={(e) => {
                        const value = Number.parseInt(e.target.value) || 1;
                        // Enforce 12 month limit
                        setMonthsToLoad(Math.min(value, 12));
                      }}
                      style={{
                        width: '50px',
                        padding: '4px',
                        border: monthsToLoad > 12 ? `1px solid ${devPanel.error}` : `1px solid ${devPanel.border}`,
                        borderRadius: '4px',
                        background: devPanel.backgroundAlt,
                        color: '#ffffff',
                        textAlign: 'center'
                      }}
                      disabled={dataLoadingStatus.isLoading}
                    />
                  </>
                ) : (
                  // Time range mode
                  <Typography variant="caption" sx={{ color: 'white' }}>Using datetime range from pickers above</Typography>
                )}
                <MuiButton
                  onClick={handleLoadData}
                  disabled={dataLoadingStatus.isLoading || !config}
                  variant="contained"
                  size="small"
                  sx={{ fontSize: '11px', minWidth: '70px' }}
                >
                  {dataLoadingStatus.isLoading ? 'Loading...' : 'Load Data'}
                </MuiButton>

                <Box sx={{
                  background: devPanel.background,
                  padding: 1,
                  borderRadius: 1,
                  fontSize: '12px',
                  color: dataLoadingStatus.isLoading ? devPanel.infoBlue : devPanel.success
                }}>
                  {dataLoadingStatus.error && (
                    <Typography variant="caption" sx={{ color: devPanel.error }}>
                      Error: {dataLoadingStatus.error}
                    </Typography>
                  )}

                </Box>
              </Box>
            </>
          )}
        </Box>
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          {/* DateTime Pickers */}
          <Box sx={{
            display: 'flex',
            gap: 2,
            mb: 2,
            mt: 1,
            alignItems: 'center',
            flexWrap: 'wrap',
            padding: useTimeRange ? 2 : 1,
            border: useTimeRange ? `2px solid ${devPanel.infoBlue}` : '1px solid transparent',
            borderRadius: 2,
            backgroundColor: useTimeRange ? devPanel.infoBlueOverlay : 'transparent',
            transition: 'all 0.3s ease'
          }}>
            {useTimeRange && (
              <Typography variant="caption" sx={{
                color: devPanel.infoBlue,
                fontWeight: 'bold',
                fontSize: '12px',
                width: '100%',
                textAlign: 'center',
                mb: 1
              }}>
                📅 Time Range Mode Active - Select dates below for data loading
              </Typography>
            )}
            <DateTimePicker
              label="Start Date & Time"
              value={startDateTime}
              onChange={handleStartDateTimeChange}
              ampm={false}
              format="DD-MM-YYYY HH:mm"
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  sx={{
                    '& .MuiInputBase-input': {
                      color: '#ffffff',
                      fontSize: '14px'
                    },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: useTimeRange ? devPanel.activeBg : devPanel.backgroundAlt,
                      '& fieldset': {
                        borderColor: useTimeRange ? devPanel.infoBlue : devPanel.border,
                      },
                      '&:hover fieldset': {
                        borderColor: devPanel.infoBlueLight,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: devPanel.infoBlueLight,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: useTimeRange ? devPanel.infoBlueLight : devPanel.textMuted,
                      fontSize: '12px'
                    }
                  }}
                />
              )}
            />
            <DateTimePicker
              label="End Date & Time"
              value={endDateTime}
              onChange={handleEndDateTimeChange}
              ampm={false}
              format="DD-MM-YYYY HH:mm"
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  sx={{
                    '& .MuiInputBase-input': {
                      color: '#ffffff',
                      fontSize: '14px'
                    },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: useTimeRange ? devPanel.activeBg : devPanel.backgroundAlt,
                      '& fieldset': {
                        borderColor: useTimeRange ? devPanel.infoBlue : devPanel.border,
                      },
                      '&:hover fieldset': {
                        borderColor: devPanel.infoBlueLight,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: devPanel.infoBlueLight,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: useTimeRange ? devPanel.infoBlueLight : devPanel.textMuted,
                      fontSize: '12px'
                    }
                  }}
                />
              )}
            />
          </Box>


        </Box>
      </Box>
    </LocalizationProvider>
  );
};


export default withTranslation("common")(DateTimeFilter);